import { Link } from "react-router-dom";
import { Workflow, FileText, Wallet, Clock, Users, PlugZap, LayoutDashboard, Building2, Sparkles, ArrowRight } from "lucide-react";

const examples = [
  { icon: Workflow, t: "Custom Approval Workflows", d: "Multi-level, delegated, conditional or matrix approvals — mirror your org chart exactly." },
  { icon: FileText, t: "Bespoke Forms", d: "Dynamic forms for anything from field surveys to grievance intake." },
  { icon: Wallet, t: "Payroll Rules", d: "State-specific taxation, custom shift premiums, industry-specific pay heads." },
  { icon: Clock, t: "Attendance Policies", d: "Overnight shifts, split shifts, compensatory offs — configured to your reality." },
  { icon: Users, t: "Employee Lifecycle Automation", d: "Trigger onboarding, transfers or offboarding based on your unique triggers." },
  { icon: PlugZap, t: "Integrations", d: "Connect legacy ERPs, accounting systems, LMS, and internal tools securely." },
  { icon: LayoutDashboard, t: "Custom Dashboards", d: "Role-specific dashboards for CEOs, plant heads, HR ops, and finance." },
  { icon: Building2, t: "Department Logic", d: "Different rules per department, plant, brand, or subsidiary — all in one system." },
  { icon: Sparkles, t: "AI & Automation", d: "Automated regularization, anomaly detection, predictive attrition alerts." },
];

export default function CustomDevelopment() {
  return (
    <div data-testid="page-custom">
      <section className="py-20 md:py-28 hero-grid-bg">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#00A651] mb-4">Custom Development</div>
          <h1 className="text-4xl md:text-6xl font-extrabold tracking-tighter text-[#1F1E4E] leading-[1.05]">
            Your HR is unique. Your HRMS should be too.
          </h1>
          <p className="mt-6 text-lg text-slate-600 leading-relaxed">
            Every organization has quirks that off-the-shelf software cannot solve. We ship custom modules, workflows, and integrations that fit — not force — your process.
          </p>
          <div className="mt-8 flex justify-center gap-3 flex-wrap">
            <Link to="/contact?intent=contact_sales" data-testid="custom-request-btn" className="bg-[#00A651] hover:bg-[#008A43] text-white font-bold rounded-lg px-6 py-3">
              Request Custom Feature
            </Link>
            <Link to="/contact?intent=book_demo" className="border-2 border-[#1F1E4E] text-[#1F1E4E] font-bold rounded-lg px-6 py-3">
              Book a Consultation
            </Link>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-2xl mb-12">
            <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight text-[#1F1E4E]">Where we customize.</h2>
            <p className="mt-4 text-slate-600">A few examples of the custom work we've shipped for customers across industries.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {examples.map((e, i) => {
              const I = e.icon;
              return (
                <div key={i} className="bg-[#F8FAFC] rounded-2xl border border-slate-200 p-7 hover:-translate-y-1 hover:shadow-md transition-all">
                  <div className="h-11 w-11 rounded-xl bg-[#E6F6EC] flex items-center justify-center mb-5">
                    <I className="h-5 w-5 text-[#00A651]" />
                  </div>
                  <div className="text-lg font-extrabold text-[#1F1E4E]">{e.t}</div>
                  <div className="text-sm text-slate-600 mt-2 leading-relaxed">{e.d}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#1F1E4E] text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight">Our Customization Process</h2>
          <p className="mt-3 text-white/70">Discovery → Design → Build → Launch. Transparent milestones every step.</p>
          <div className="mt-10 grid md:grid-cols-4 gap-5 text-left">
            {["Discovery", "Design & Estimate", "Build & Test", "Launch & Support"].map((s, i) => (
              <div key={s} className="bg-white/5 rounded-2xl p-6 border border-white/10">
                <div className="text-xs font-bold text-[#00A651]">STEP {i + 1}</div>
                <div className="text-lg font-extrabold mt-1">{s}</div>
                <div className="text-sm text-white/70 mt-2">
                  {["We understand your workflows and constraints in depth.", "Detailed solution design with timeline and fixed pricing.", "Iterative build with sprint demos every 2 weeks.", "Go live with training, docs, and ongoing support."][i]}
                </div>
              </div>
            ))}
          </div>
          <Link to="/contact?intent=contact_sales" className="inline-flex items-center gap-2 mt-10 bg-[#00A651] hover:bg-[#008A43] text-white font-bold rounded-lg px-6 py-3" data-testid="custom-schedule-btn">
            Schedule a Consultation <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
